import React from 'react';
import { seoData } from '@/data/seoTopics';
import { motion } from 'framer-motion';
import { SEO } from '@/components/SEO';
import { ChevronRight, ArrowLeft } from 'lucide-react';

export const ExplorePage = ({ onNavigate, path }: { onNavigate: (path: string) => void, path: string }) => {
  // Path format: /explore/mathematics or /explore/mathematics/core-math
  const parts = path.split('/').filter(Boolean);
  const subjectSlug = parts[1];
  const topicSlug = parts[2];

  const subject = seoData.find(s => s.subjectSlug === subjectSlug);

  if (!subject) return <div className="text-white p-12 text-center text-xl">Subject not found</div>;

  if (topicSlug) {
    const topic = subject.topics.find(t => t.topicSlug === topicSlug);
    if (!topic) return <div className="text-white p-12 text-center text-xl">Topic not found</div>;

    return (
      <>
        <SEO 
          title={`${topic.topic} Practice Questions | ${subject.subject} | ProfessorXAI`}
          description={`Practice ${topic.topic} questions with step-by-step AI solutions. Master ${subject.subject} for competitive exams.`}
          canonical={`https://professorxai.com/explore/${subject.subjectSlug}/${topic.topicSlug}`}
        />
        <div className="max-w-5xl mx-auto px-6 py-12 text-slate-200">
          <nav className="flex items-center space-x-2 text-sm text-slate-400 mb-8">
            <button onClick={() => onNavigate('/')} className="hover:text-white">Home</button>
            <ChevronRight className="w-4 h-4" />
            <button onClick={() => onNavigate('/topics')} className="hover:text-white">Topics</button>
            <ChevronRight className="w-4 h-4" />
            <button onClick={() => onNavigate(`/explore/${subject.subjectSlug}`)} className="hover:text-white">{subject.subject}</button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-indigo-400">{topic.topic}</span>
          </nav>
          
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">{topic.topic}</h1>
          <p className="text-slate-400 text-lg mb-12 max-w-2xl">Master {topic.topic} with our comprehensive collection of practice questions and detailed AI-generated step-by-step solutions.</p>

          <div className="space-y-4">
            {topic.questions.map((q, idx) => (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                key={q.slug}
                onClick={() => onNavigate(`/question/${q.slug}`)}
                className="bg-slate-900/40 p-6 rounded-2xl border border-white/5 hover:border-indigo-500/30 hover:bg-slate-800/50 cursor-pointer transition-all flex items-center justify-between group"
              >
                <div className="flex-1 pr-6">
                  <h3 className="text-lg font-bold text-slate-200 group-hover:text-white mb-2">{q.q}</h3>
                  <p className="text-slate-500 text-sm line-clamp-2">{q.a}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-indigo-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-500/20 transition-colors">
                  <ChevronRight className="w-5 h-5 text-indigo-400 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title={`${subject.subject} Important Topics & Questions | ProfessorXAI`}
        description={`Explore ${subject.subject} topics. Over thousands of practice questions and step-by-step solutions.`}
        canonical={`https://professorxai.com/explore/${subject.subjectSlug}`}
      />
      <div className="max-w-5xl mx-auto px-6 py-12 text-slate-200">
        <button onClick={() => onNavigate('/topics')} className="text-indigo-400 hover:text-indigo-300 flex items-center gap-2 font-medium mb-8">
          <ArrowLeft className="w-5 h-5" /> Back to All Subjects
        </button>
        <h1 className="text-5xl md:text-6xl font-black text-white mb-6">{subject.subject}</h1>
        <p className="text-xl text-slate-400 mb-12 max-w-2xl">Select a topic to start practicing questions.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {subject.topics.map((t, idx) => (
             <motion.div
               key={t.topicSlug}
               initial={{ opacity: 0, scale: 0.95 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ delay: idx * 0.05 }}
               onClick={() => onNavigate(`/explore/${subject.subjectSlug}/${t.topicSlug}`)}
               className="bg-gradient-to-br from-slate-900/80 to-[#0a0a0f] p-8 rounded-3xl border border-white/5 hover:border-indigo-500/50 cursor-pointer shadow-lg hover:shadow-[0_0_30px_rgba(79,70,229,0.15)] transition-all group"
             >
               <h2 className="text-2xl font-bold text-white mb-4 group-hover:text-indigo-300 transition-colors">{t.topic}</h2>
               <div className="flex items-center justify-between text-slate-400 text-sm">
                 <span>{t.questions.length} Practice Questions</span>
                 <ChevronRight className="w-5 h-5 text-indigo-500 group-hover:translate-x-1 transition-transform" />
               </div>
             </motion.div>
          ))}
        </div>
      </div>
    </>
  );
};
